import api from './api';

/**
 * OAuth Service for Git Provider Authentication
 * Handles OAuth flow for GitHub, GitLab, and Bitbucket
 */
class OAuthService {
  /**
   * Initiate OAuth flow for a provider
   * @param {string} provider - 'github', 'gitlab', or 'bitbucket'
   * @param {string} gitlabUrl - Optional custom GitLab URL (for self-hosted instances)
   * @returns {Promise<{authorization_url: string, state: string, provider: string}>}
   */
  async initiateOAuth(provider, gitlabUrl = null) {
    try {
      const params = {};
      if (provider === 'gitlab' && gitlabUrl) {
        params.gitlab_url = gitlabUrl;
      }

      const response = await api.get(`/oauth/authorize/${provider}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error initiating OAuth for ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Handle OAuth callback
   * @param {string} provider - Provider name
   * @param {string} code - Authorization code
   * @param {string} state - State token
   * @returns {Promise<{success: boolean, credential_id: string, provider: string, username: string}>}
   */
  async handleCallback(provider, code, state) {
    try {
      const response = await api.post(
        `/oauth/callback/${provider}`,
        null,
        {
          params: { code, state }
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Error handling OAuth callback for ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Get list of repositories from provider
   * @param {string} provider - Provider name
   * @returns {Promise<Array>}
   */
  async getRepositories(provider) {
    try {
      const response = await api.get(`/oauth/${provider}/repositories`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching repositories for ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Get OAuth authentication status
   * @param {string} provider - Provider name
   * @returns {Promise<{is_authenticated: boolean, username?: string, email?: string}>}
   */
  async getStatus(provider) {
    try {
      const response = await api.get(`/oauth/${provider}/status`);
      return response.data;
    } catch (error) {
      console.error(`Error getting OAuth status for ${provider}:`, error);
      // Return not authenticated on error
      return { is_authenticated: false, provider };
    }
  }

  /**
   * Disconnect OAuth provider
   * @param {string} provider - Provider name
   * @returns {Promise<{success: boolean}>}
   */
  async disconnect(provider) {
    try {
      const response = await api.delete(`/oauth/${provider}/disconnect`);
      return response.data;
    } catch (error) {
      console.error(`Error disconnecting ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Manually refresh access token
   * @param {string} provider - Provider name
   * @returns {Promise<{success: boolean}>}
   */
  async refreshToken(provider) {
    try {
      const response = await api.post(`/oauth/${provider}/refresh`);
      return response.data;
    } catch (error) {
      console.error(`Error refreshing token for ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Open OAuth popup window and wait for callback
   * @param {string} authUrl - Authorization URL
   * @returns {Promise<{code: string, state: string}>}
   */
  openOAuthPopup(authUrl) {
    return new Promise((resolve, reject) => {
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        authUrl,
        'OAuth Authentication',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      if (!popup) {
        reject(new Error('Popup blocked. Please allow popups for this site.'));
        return;
      }

      // Listen for OAuth callback message
      const messageHandler = (event) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data.type === 'oauth_callback') {
          window.removeEventListener('message', messageHandler);

          if (popup && !popup.closed) {
            popup.close();
          }

          if (event.data.code && event.data.state) {
            resolve({
              code: event.data.code,
              state: event.data.state
            });
          } else if (event.data.error) {
            reject(new Error(event.data.error || 'OAuth authorization failed'));
          } else {
            reject(new Error('OAuth callback missing required parameters'));
          }
        }
      };

      window.addEventListener('message', messageHandler);

      // Check if popup was closed before completion
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          reject(new Error('OAuth popup was closed before completing authentication'));
        }
      }, 1000);

      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(checkClosed);
        window.removeEventListener('message', messageHandler);
        if (popup && !popup.closed) {
          popup.close();
        }
        reject(new Error('OAuth flow timed out'));
      }, 5 * 60 * 1000);
    });
  }

  /**
   * Complete OAuth flow (initiate + popup + callback)
   * @param {string} provider - Provider name
   * @param {string} gitlabUrl - Optional custom GitLab URL (for self-hosted instances)
   * @returns {Promise<{success: boolean, credential_id: string, username: string}>}
   */
  async completeOAuthFlow(provider, gitlabUrl = null) {
    try {
      // Step 1: Initiate OAuth (with optional GitLab URL)
      const { authorization_url, state } = await this.initiateOAuth(provider, gitlabUrl);

      // Step 2: Open popup and wait for callback
      const { code, state: returnedState } = await this.openOAuthPopup(authorization_url);

      // Step 3: Handle callback
      const result = await this.handleCallback(provider, code, returnedState);

      return result;
    } catch (error) {
      console.error(`Error completing OAuth flow for ${provider}:`, error);
      throw error;
    }
  }
}

// Export singleton instance
const oauthService = new OAuthService();
export default oauthService;
